// utils/regionPricing.ts — Kelompok harga region + blokir provinsi (produk)
import { AD_ZONES } from '../data/adZones';
import type { RegionGroup, RegionPriceMode } from '../types/forms/fields';

export type { RegionGroup, RegionPriceMode };

// Semua provinsi kanonik (ibnux) dari AD_ZONES.
export const ALL_PROVINCES: string[] = AD_ZONES.flatMap(z => z.provinces);

// Kanonik provinsi: varian ejaan → 1 kunci, WAJIB equality (bukan substring).
// 'Riau' ⊂ 'Kepulauan Riau' + ejaan pendek/panjang bikin dropdown/harga bocor (insiden Z2/Z3 2026-10-13).
const CANON_MAP: Record<string, string> = {
    daerahkhususibukotajakarta: 'jakarta', dkijakarta: 'jakarta',
    daerahistimewayogyakarta: 'yogyakarta', diyogyakarta: 'yogyakarta',
    kepulauanriau: 'kepri', kepri: 'kepri',
    kepulauanbangkabelitung: 'babel', bangkabelitung: 'babel',
};

export function canonProvince(s: string | null | undefined): string {
    let k = (s || '').toLowerCase().replace(/[^a-z]/g, '').replace(/sumatera/g, 'sumatra');
    return CANON_MAP[k] || k;
}

function namesMatch(a: string, b: string): boolean {
    const x = canonProvince(a), y = canonProvince(b);
    return !!x && !!y && x === y;
}

// Grup pertama yang cocok menang; grup tanpa provinsi = catch-all (default).
export function findRegionGroup(groups: RegionGroup[] | undefined, province?: string | null): RegionGroup | null {
    if (!groups || groups.length === 0) return null;
    if (!province) return groups.find(g => (g.provinces || []).length === 0) || null;
    const hit = groups.find(g => (g.provinces || []).some(p => namesMatch(p, province)));
    if (hit) return hit;
    return groups.find(g => (g.provinces || []).length === 0) || null;
}

// Harga final: override region bila ada, else harga dasar.
export function resolveRegionPrice(base: number, regionPrices: Record<string, number> | undefined, groupId: string | null): number {
    if (groupId && regionPrices && typeof regionPrices[groupId] === 'number' && regionPrices[groupId]! > 0) return regionPrices[groupId]!;
    return base;
}

export function isProvinceBlocked(blocked: string[] | undefined, province?: string | null): boolean {
    if (!blocked || blocked.length === 0 || !province) return false;
    return blocked.some(p => namesMatch(p, province));
}

export function provinceMatches(a: string, b: string): boolean {
    return namesMatch(a, b);
}

export interface RegionLockLike {
    productRegionGroups?: RegionGroup[];
    productBlockedProvinces?: string[];
    regionLock?: { mode?: string; groupIds?: string[] };
    shippingSettings?: { allowedProvinces?: string[] | null };
}

// Grup efektif: mode khusus → hanya grup terpilih (id basi diabaikan);
// khusus tanpa id valid → fallback semua (anti lock-out total).
export function effectiveGroups(form: RegionLockLike | null | undefined): RegionGroup[] {
    const groups = form?.productRegionGroups || [];
    const lock = form?.regionLock;
    if (lock?.mode === 'custom') {
        const picked = groups.filter(g => (lock.groupIds || []).includes(g.id));
        return picked.length > 0 ? picked : groups;
    }
    return groups;
}

// Satu-satunya grup (kunci jelas) → id-nya; else null.
export function singleLockGroup(form: RegionLockLike | null | undefined): RegionGroup | null {
    const eff = effectiveGroups(form);
    return eff.length === 1 ? eff[0] : null;
}

// Grup untuk 1 provinsi (single-lock menang tanpa perlu provinsi).
export function resolveGroupId(form: RegionLockLike | null | undefined, province?: string | null): string | null {
    const single = singleLockGroup(form);
    if (single) return single.id;
    return findRegionGroup(effectiveGroups(form), province)?.id ?? null;
}

// Exclude dropdown = blokir + komplemen allowlist grup produk + komplemen allowlist shipping (null/kosong = semua).
export function computeExcludedProvinces(form: RegionLockLike | null | undefined): string[] {
    const blocked = form?.productBlockedProvinces || [];
    const eff = effectiveGroups(form);
    const noAllow = eff.length === 0 || eff.some(g => (g.provinces || []).length === 0);
    const groupAllowed = noAllow ? null : eff.flatMap(g => g.provinces || []);
    const shipAllowed = (form?.shippingSettings?.allowedProvinces || []).filter(Boolean);
    const extra = (list: string[] | null) => !list || list.length === 0 ? [] as string[]
        : ALL_PROVINCES.filter(p => !list.some(a => namesMatch(a, p)));
    const base = [...blocked, ...extra(groupAllowed).filter(x => !blocked.some(b => namesMatch(b, x)))];
    return [...base, ...extra(shipAllowed).filter(x => !base.some(b => namesMatch(b, x)))];
}
export function newRegionGroup(label = ''): RegionGroup {
    return { id: `rg_${Date.now()}_${Math.floor(Math.random() * 1e4)}`, label, provinces: [] };
}
